import { useTranslation } from 'react-i18next';
import { formatCurrency as utilFormatCurrency, formatDate as utilFormatDate, formatDateTime as utilFormatDateTime } from '@/lib/utils';
import { calculateAge } from '@/utils/calculations';

// Custom hook for type-safe translations with automatic locale detection
export const useI18n = () => {
  const { t, i18n } = useTranslation();
  
  // Get current locale for formatting
  const currentLocale = i18n.language === 'de' ? 'de-DE' : 'en-US';

  // Helper functions using current language automatically
  const formatCurrency = (amount: number): string => {
    return utilFormatCurrency(amount, currentLocale);
  };

  const formatDate = (date: string | Date): string => {
    return utilFormatDate(date, currentLocale);
  };

  const formatDateTime = (date: string | Date): string => {
    return utilFormatDateTime(date, currentLocale);
  };

  const formatAge = (dateOfBirth: string | Date): string => {
    const age = calculateAge(dateOfBirth);
    return `${age} ${t('participant.years')}`;
  };

  // Currency helpers
  const currency = {
    symbol: t('currencies.eur'),
    perPerson: t('currencies.perPerson'),
    perWeek: t('currencies.perWeek'),
  };

  // Common form labels
  const labels = {
    name: t('common.name'),
    email: t('common.email'),
    phone: t('common.phone'),
    address: t('common.address'),
    dateOfBirth: t('common.dateOfBirth'),
    required: t('common.required'),
    optional: t('common.optional'),
  };

  // Common actions
  const actions = {
    save: t('common.save'),
    cancel: t('common.cancel'),
    delete: t('common.delete'),
    edit: t('common.edit'),
    add: t('common.add'),
    remove: t('common.remove'),
    close: t('common.close'),
    submit: t('common.submit'),
    confirm: t('common.confirm'),
  };

  // Status messages
  const status = {
    loading: t('common.loading'),
    success: t('success.saved'),
    error: t('errors.general'),
  };

  return {
    t,
    i18n,
    formatCurrency,
    formatDate,
    formatDateTime,
    formatAge,
    calculateAge, // Re-export for convenience
    currency,
    labels,
    actions,
    status,
    isGerman: i18n.language === 'de',
    isEnglish: i18n.language === 'en',
    currentLocale, // Expose current locale
  };
};

export default useI18n;
